import { Icon } from '@/shared/ui'
import { useAiDraftReview } from '../model/use-ai-draft-review'
import s from './AiDraftReview.module.css'

type AiDraftReviewProps = {
  conversationId: string
  /**
   * «Править» — текст черновика уезжает в композер (`features/message-send`, `send.edit`).
   * Фича не может импортировать соседнюю фичу (FSD), поэтому связь идёт через общего родителя —
   * виджет, который обе и составляет.
   */
  onEdit: (text: string) => void
}

/**
 * Карточка черновика консьержа в ленте «Чатов» и карточки брони. Вид — из макета до `1adc427`
 * (`Prototype.dc.html:1562`): в нынешнем `prototype/` разметки карточки нет, остались её состояния
 * и обработчики (`c3DraftOpen`, `sendDraft`, `dismissDraft`) и режим «Черновики» в настройках.
 *
 * Черновик приезжает в шапке треда (`ConversationDetail.aiDraft`), а не элементом ленты, и живёт
 * ровно один: сервер держит либо один висящий черновик, либо ни одного.
 *
 * **Здесь только вид.** Отметка «отправлен», отказ и то, какой черновик называют кнопки, — в
 * `model/use-ai-draft-review.ts`.
 *
 * **Три кнопки — три разные ручки контракта, и ни одна не выдумана:**
 *
 * - «Отправить» → `POST /ai-draft/send` с id черновика на экране; уходит он целиком.
 * - «Отклонить» → `POST /ai-draft/dismiss` с тем же id. Наружу ничего не уходит.
 * - «Править» → **черновик не редактируется в принципе** (контракт, `paths/conversations.yaml`):
 *   «Править черновик нельзя — для этого переписку переводят в режим manager и отправляют текст
 *   обычным sendMessage». Ровно это кнопка и делает: переводит режим (сервер при этом сам
 *   отклоняет висящий черновик) и кладёт его текст в композер, чтобы менеджер правил готовое, а не
 *   набирал заново. В макете у этой кнопки обработчика нет вовсе — но и мёртвой кнопки в проде
 *   быть не должно, а честная работа для неё в контракте описана прямо.
 */
export function AiDraftReview({ conversationId, onEdit }: AiDraftReviewProps) {
  const { draft, sent, refusal, busy, send, dismiss, edit } = useAiDraftReview(conversationId)

  // Отказ «Отправить» — строкой в карточке, а без черновика — на её месте: общий тост молчит, пока
  // карточка следит за отправкой (`meta.handledInPlace`), и строка — единственное, что о нём скажет.
  const refusalLine = refusal ? (
    <p className={s.refusal} role="alert">
      {refusal}
    </p>
  ) : null

  if (!draft) {
    /*
     * Черновика нет. Если последний отправили мы — рисуем отметку из макета (`c3DraftSent`). Она
     * честна ровно в этом случае: `sent` ставится только `201`-м ответом и только для своей
     * переписки. Провал, `409` и успех у СОСЕДА сюда не попадают.
     */
    return sent ? (
      <div className={s.sent}>
        <Icon name="check" size={12} strokeWidth={2.6} />
        Черновик консьержа отправлен
      </div>
    ) : (
      refusalLine
    )
  }

  return (
    <div className={s.root}>
      <div className={s.head}>
        <span className={s.tile}>
          <Icon name="spark" size={12} strokeWidth={1.8} />
        </span>
        <span className={s.title}>Консьерж предложил ответ</span>
        {/* Что ИИ видел, готовя ответ, говорит СЕРВЕР (`aiDraft.safetyLabel`): это утверждение о
            его доступе, и знает о нём только он. В макете на этом месте литерал. */}
        <span className={s.safety}>{draft.safetyLabel}</span>
      </div>

      <div className={s.text}>{draft.text}</div>

      <div className={s.actions}>
        <button type="button" className={s.primary} disabled={busy} onClick={send}>
          Отправить
        </button>

        <button
          type="button"
          className={s.secondary}
          disabled={busy}
          onClick={() => {
            /*
             * Текст кладём в композер СРАЗУ, не дожидаясь ответа сервера: он уже у нас на руках,
             * и терять его из-за упавшей сети незачем — `sendMessage` работает в любом режиме.
             * Смена режима — то, что предписывает контракт: менеджер взял переписку на себя,
             * сервер отклоняет черновик, карточка исчезает сама (мутация кладёт свежую шапку
             * в кеш). Провал переключения покажет тост (`MutationCache.onError`).
             */
            edit(onEdit)
          }}
        >
          Править
        </button>

        <button
          type="button"
          className={`${s.secondary} ${s.dismiss}`}
          disabled={busy}
          onClick={dismiss}
        >
          Отклонить
        </button>
      </div>

      {refusalLine}
    </div>
  )
}
